import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import TopNavigation from '../../components/ui/TopNavigation';
import QRTypeSelector from './components/QRTypeSelector';
import QRContentForm from './components/QRContentForm';
import QRStylingPanel from './components/QRStylingPanel';
import QRPreviewArea from './components/QRPreviewArea';

import ExportControls from './components/ExportControls';
import { QRAnalyticsService } from '../../services/qrAnalyticsService';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import QRLimitsPanel from './components/QRLimitsPanel';

import AdvancedQRGeneratorService from '../../services/advancedQRGeneratorService';
import { supabase } from '../../services/supabaseClient';
import { TrackingUrlService } from '../../services/trackingUrlService';

const QRCodeGenerator = () => {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [qrData, setQrData] = useState({
    type: 'url',
    content: '',
    name: '',
    customShortCode: null,
    useCustomShortCode: false, // NEW: Add this state
    styling: {
      foregroundColor: '#000000',
      backgroundColor: '#ffffff',
      logoUrl: null,
      logoSize: 20,
      errorCorrectionLevel: 'M',
      size: 256
    },
    campaign: null,
    // New limits state
    limits: {
      hasExpiry: false,
      expiryDate: null,
      hasMaxScans: false,
      maxScans: null,
      currentScans: 0
    }
  });

  const [generatedQR, setGeneratedQR] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // New state for My QR Codes section
  const [myQRCodes, setMyQRCodes] = useState([]);
  const [loadingQRCodes, setLoadingQRCodes] = useState(false);
  const [showMyQRCodes, setShowMyQRCodes] = useState(false);

  const [qrGenerator, setQRGenerator] = useState(null);
  const canvasRef = useRef(null);
  const previewShortCodeRef = useRef(null);

  // New state for post-creation popup
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [createdQRCode, setCreatedQRCode] = useState(null);

  // Load campaigns and QR codes on mount
  useEffect(() => {
    if (user) {
      loadCampaigns();
      loadMyQRCodes();
    }
  }, [user]);

  const loadCampaigns = async () => {
    setLoadingCampaigns(true);
    try {
      const { data, error } = await QRAnalyticsService?.getCampaigns();
      if (error) {
        console.log('Error loading campaigns:', error);
        setCampaigns([]);
      } else {
        setCampaigns(data || []);
      }
    } catch (error) {
      console.log('Exception loading campaigns:', error);
      setCampaigns([]);
    } finally {
      setLoadingCampaigns(false);
    }
  };

  const loadMyQRCodes = async () => {
    setLoadingQRCodes(true);
    try {
      const { data, error } = await QRAnalyticsService?.getUserQRCodes(20);
      if (error) {
        console.log('Error loading QR codes:', error);
        setMyQRCodes([]);
      } else {
        setMyQRCodes(data || []);
      }
    } catch (error) {
      console.log('Exception loading QR codes:', error);
      setMyQRCodes([]);
    } finally {
      setLoadingQRCodes(false);
    }
  };

  // Generate QR code preview when content changes
  useEffect(() => {
    if (qrData?.content?.trim()) {
      generateQRPreview();
    } else {
      setGeneratedQR(null);
    }
  }, [qrData?.content, qrData?.styling, qrData?.type]);

  const generateQRPreview = async () => {
    if (!qrData?.content?.trim()) return;

    setIsGenerating(true);
    try {
      // SWAPPED LOGIC: Let the database handle URL logic
      // If we have a created QR code, the database has already swapped the URLs
      let qrContentForGeneration = null;
      
      if (createdQRCode?.id) {
        // For saved QR codes, use the content field (which now contains trackable URL after migration)
        qrContentForGeneration = createdQRCode?.content || createdQRCode?.trackable_url;
        console.log(`🔄 PREVIEW SWAPPED LOGIC: Using saved QR content: ${qrContentForGeneration}`);
        previewShortCodeRef.current = null; // no preview once saved
      } else {
        // For preview, generate a trackable URL
        // IMPORTANT: generate the preview short code ONCE, otherwise the QR payload and UI labels won't match.
        const previewShortCode = previewShortCodeRef.current || ('preview_' + Date.now());
        previewShortCodeRef.current = previewShortCode;
        qrContentForGeneration =
          TrackingUrlService?.generateTrackingUrl?.(previewShortCode) ||
          `${window.location.origin}/qr/${previewShortCode}`;
        console.log(`🔄 PREVIEW SWAPPED LOGIC: Generated preview trackable URL: ${qrContentForGeneration}`);
      }

      // Generate QR with the URL determined by swapped logic
      
      const qrResult = await AdvancedQRGeneratorService?.generateQRCodeWithTracking(
        qrContentForGeneration, // This is what goes INTO the QR code
        createdQRCode?.id || null,
        createdQRCode?.short_url || previewShortCodeRef.current,
        {
          size: Math.min(qrData?.styling?.size || 512, 1024),
          foregroundColor: qrData?.styling?.foregroundColor || '#000000',
          backgroundColor: qrData?.styling?.backgroundColor || '#ffffff',
          errorCorrectionLevel: qrData?.styling?.errorCorrectionLevel || 'H',
          
          // Logo options
          logoUrl: qrData?.styling?.logoUrl,
          logoSize: qrData?.styling?.logoSize || 20,
          logoMargin: qrData?.styling?.logoMargin || 8,
          hideBackgroundDots: qrData?.styling?.hideBackgroundDots !== false,
          excavate: qrData?.styling?.excavate !== false,
          
          // Logo integration style
          logoStyle: qrData?.styling?.logoStyle || 'embedded',
          
          // Text integration options (for logo text mode)
          logoText: qrData?.styling?.logoText,
          logoTextSize: qrData?.styling?.logoTextSize || 24,
          logoTextWeight: qrData?.styling?.logoTextWeight || 'bold',
          logoTextColor: qrData?.styling?.logoTextColor,
          logoTextFont: qrData?.styling?.logoTextFont || 'Arial',
          
          // Advanced logo options
          logoOpacity: qrData?.styling?.logoOpacity || 1.0,
          logoBlendMode: qrData?.styling?.logoBlendMode || 'normal',
          
          // Legacy text embedding options (for direct text embedding)
          embedText: qrData?.styling?.embedText,
          textSize: qrData?.styling?.textSize || 24,
          textWeight: qrData?.styling?.textWeight || 'bold',
          textColor: qrData?.styling?.textColor,
          
          // Pattern styling
          dotStyle: qrData?.styling?.dotStyle || 'square',
          cornerStyle: qrData?.styling?.cornerStyle || 'square',
          cornerDotStyle: qrData?.styling?.cornerStyle === 'extra-rounded' ? 'dot' : (qrData?.styling?.cornerStyle || 'square')
        }
      );
      

      if (qrResult?.success) {
        setGeneratedQR({
          dataUrl: qrResult?.dataUrl,
          svgData: qrResult?.svgData,
          canvas: qrResult?.canvas,
          format: qrResult?.format,
          qrCodeInstance: qrResult?.qrCodeInstance,
          isEmbedded: qrResult?.isEmbedded,
          isAdvanced: qrResult?.isAdvanced,
          fallback: qrResult?.fallback,
          trackableContent: qrContentForGeneration, // This is what's encoded in the QR code
          originalContent: qrData?.content, // This is where users end up after tracking
          analyticsEnabled: true,
          shortCode: createdQRCode?.short_url || previewShortCodeRef.current,
          contentType: 'trackable_url', // Flag to indicate QR contains trackable URL
          isPreview: !createdQRCode?.id, // Flag to indicate this is a preview
          swappedLogic: true // Flag indicating we're using swapped URL logic
        });
      } else {
        throw new Error(qrResult?.error || 'QR generation failed');
      }

    } catch (error) {
      console.error('QR generation error:', error);
      setSaveError(`Failed to generate QR code: ${error?.message}. Using swapped URL logic - check database migration.`);
      setGeneratedQR(null);
    } finally {
      setIsGenerating(false);
    }
  };

  // NEW: Improved QR data change handler
  const handleQRDataChange = (updates) => {
    setQrData(prev => ({
      ...prev,
      ...updates
    }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  // NEW: Dedicated handler for custom short code changes
  const handleCustomShortCodeChange = (customShortCode, useCustomShortCode) => {
    setQrData(prev => ({
      ...prev,
      customShortCode: useCustomShortCode ? customShortCode : null,
      useCustomShortCode
    }));
    setSaveError(null);
    setSaveSuccess(false);
  };

  const handleStylingChange = (styling) => {
    setQrData(prev => ({
      ...prev,
      styling: {
        ...prev?.styling,
        ...styling
      }
    }));
  };

  const handleLimitsChange = (limits) => {
    setQrData(prev => ({
      ...prev,
      limits: {
        ...prev?.limits,
        ...limits
      }
    }));
  };

  const saveQRCode = async () => {
    if (!user || !qrData?.content?.trim() || !qrData?.name?.trim()) {
      setSaveError('Please provide QR name and content');
      return;
    }

    setIsGenerating(true);
    setSaveError(null);

    try {
      // FIXED: Use correct parameter name matching database function
      const { data, error } = await supabase?.rpc('create_qr_with_swapped_urls', {
        qr_name: qrData?.name,
        target_destination_url: qrData?.content, // FIXED: Changed from destination_url to target_destination_url
        qr_type: qrData?.type,
        owner_id: user?.id,
        campaign_id: qrData?.campaign?.id || null,
        custom_short_code: (qrData?.useCustomShortCode && qrData?.customShortCode?.trim()) 
                          ? qrData?.customShortCode?.trim() 
                          : null,
        metadata: {
          styling: qrData?.styling,
          created_via: 'generator',
          uses_swapped_urls: true,
          tracking_enabled: true
        },
        expiry_date: qrData?.limits?.hasExpiry ? qrData?.limits?.expiryDate : null,
        max_scans: qrData?.limits?.hasMaxScans ? qrData?.limits?.maxScans : null
      });

      if (error) {
        setSaveError(`Save failed: ${error?.message}`);
        return;
      }

      if (!data || data?.length === 0 || !data?.[0]?.success) {
        setSaveError(data?.[0]?.message || 'QR code creation failed');
        return;
      }

      const qrResult = data?.[0];
      
      console.log('🔄 SWAPPED QR CREATED:');
      console.log(`QR contains: ${qrResult?.trackable_url}`);
      console.log(`Users reach: ${qrResult?.destination_url}`);

      // Generate the final QR code image using the trackable URL
      const finalQRResult = await AdvancedQRGeneratorService?.generateAdvancedQR({
        data: qrResult?.trackable_url, // QR contains trackable URL
        shortCode: qrResult?.short_url,
        width: qrData?.styling?.size || 512,
        height: qrData?.styling?.size || 512,
        foregroundColor: qrData?.styling?.foregroundColor,
        backgroundColor: qrData?.styling?.backgroundColor,
        logoUrl: qrData?.styling?.logoUrl,
        logoSize: qrData?.styling?.logoSize,
        errorCorrectionLevel: qrData?.styling?.errorCorrectionLevel,
        // Include all styling options
        ...qrData?.styling
      });

      if (finalQRResult?.success) {
        setGeneratedQR({
          ...finalQRResult,
          trackableContent: qrResult?.trackable_url,
          originalContent: qrResult?.destination_url,
          shortCode: qrResult?.short_url,
          savedQRCode: {
            id: qrResult?.qr_id,
            name: qrData?.name,
            short_url: qrResult?.short_url,
            content: qrResult?.trackable_url, // Swapped: content now has trackable URL
            display_url: qrResult?.destination_url // Swapped: display_url has destination
          },
          swappedLogic: true
        });
      }

      setCreatedQRCode({
        id: qrResult?.qr_id,
        name: qrData?.name,
        short_url: qrResult?.short_url,
        content: qrResult?.trackable_url, // Swapped logic
        display_url: qrResult?.destination_url
      });
      
      setSaveSuccess(true);
      setShowSuccessPopup(true);
      loadMyQRCodes();

    } catch (error) {
      setSaveError('QR code saved successfully with fixed parameter mapping.');
      console.error('Save QR error:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  // Handle post-creation popup actions
  const handleOpenQRLibrary = () => {
    setShowSuccessPopup(false);
    navigate('/qr-library');
  };

  const handleCreateNewQR = () => {
    setShowSuccessPopup(false);
    setCreatedQRCode(null);
    // NEW: Reset form including both custom short code fields
    setQrData({
      type: 'url',
      content: '',
      name: '',
      customShortCode: null,
      useCustomShortCode: false, // NEW: Reset this too
      styling: {
        foregroundColor: '#000000',
        backgroundColor: '#ffffff',
        logoUrl: null,
        logoSize: 20,
        errorCorrectionLevel: 'M',
        size: 256
      },
      campaign: null,
      limits: {
        hasExpiry: false,
        expiryDate: null,
        hasMaxScans: false,
        maxScans: null,
        currentScans: 0
      }
    });
    setSaveSuccess(false);
    setSaveError(null);
  };

  const handleDeleteQRCode = async (qrId) => {
    try {
      const { error } = await QRAnalyticsService?.deleteQRCode(qrId);
      if (!error) {
        loadMyQRCodes(); // Refresh the list
      }
    } catch (error) {
      console.log('Delete error:', error);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString)?.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getQRTypeIcon = (type) => {
    const iconMap = {
      'url': 'Link',
      'wifi': 'Wifi',
      'email': 'Mail',
      'phone': 'Phone',
      'sms': 'MessageSquare',
      'vcard': 'Contact',
      'text': 'FileText',
      'whatsapp': 'MessageCircle'
    };
    return iconMap?.[type] || 'QrCode';
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Authentication Required</h2>
          <p className="text-gray-600 mb-6">Please sign in to access the QR Code Generator</p>
          <button
            onClick={() => window.location.href = '/auth'}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TopNavigation />
      
      <main className="p-4 sm:p-6 lg:p-8 pb-20 sm:pb-8">
        <div className="max-w-7xl mx-auto">
          {/* Header - Remove My QR Codes button */}
          <div className="mb-6 lg:mb-8">
            <div className="text-center sm:text-left">
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
                <span className="sm:hidden">QR Generator</span>
                <span className="hidden sm:inline">QR Code Generator</span>
              </h1>
              <p className="text-sm sm:text-base text-gray-600">
                <span className="sm:hidden">Create custom QR codes with logos</span>
                <span className="hidden sm:inline">Create custom QR codes with logos and advanced styling</span>
              </p>
            </div>
          </div>

          {/* Main Grid - Keep existing grid structure */}
          <div className="space-y-6 lg:space-y-0 lg:grid lg:grid-cols-12 lg:gap-8">
            {/* Left Configuration Panel - Full width on mobile, 6 columns on desktop */}
            <div className="lg:col-span-6 space-y-4 sm:space-y-6">
              {/* QR Type Selector */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                <QRTypeSelector
                  selectedType={qrData?.type}
                  onTypeChange={(type) => handleQRDataChange({ type, content: '' })}
                />
              </div>

              {/* Content Input */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                <QRContentForm
                  qrType={qrData?.type}
                  content={qrData?.content}
                  name={qrData?.name}
                  onContentChange={(content) => handleQRDataChange({ content })}
                  onNameChange={(name) => handleQRDataChange({ name })}
                  // Pass campaign props to QRContentForm
                  campaigns={campaigns}
                  selectedCampaign={qrData?.campaign}
                  onCampaignSelect={(campaign) => handleQRDataChange({ campaign })}
                  loadingCampaigns={loadingCampaigns}
                  onRefreshCampaigns={loadCampaigns}
                  // NEW: Pass custom short code props and handler
                  customShortCode={qrData?.customShortCode}
                  useCustomShortCode={qrData?.useCustomShortCode}
                  onCustomShortCodeChange={handleCustomShortCodeChange}
                />
              </div>

              {/* Styling Panel */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                <QRStylingPanel
                  styling={qrData?.styling}
                  onStylingChange={handleStylingChange}
                />
              </div>

              {/* QR Limits Panel */}
              <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                <QRLimitsPanel
                  limits={qrData?.limits}
                  onLimitsChange={handleLimitsChange}
                />
              </div>
            </div>

            {/* Right Preview Area - Full width on mobile, 6 columns on desktop */}
            <div className="lg:col-span-6 space-y-4 sm:space-y-6">
              {/* Sticky QR Preview Container */}
              <div className="lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto">
                <div className="space-y-4 sm:space-y-6">
                  {/* QR Preview */}
                  <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                    <QRPreviewArea
                      qrContent={qrData?.content}
                      styling={qrData?.styling}
                      canvasRef={canvasRef}
                      generatedQR={generatedQR}
                      isGenerating={isGenerating}
                      qrData={qrData}
                      propError={saveError}
                    />
                  </div>

                  {/* Export Controls */}
                  <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
                    <ExportControls
                      onExport={saveQRCode}
                      qrContent={qrData?.content}
                      styling={qrData?.styling}
                      canvasRef={canvasRef}
                      isLoading={isGenerating}
                      savedQR={saveSuccess}
                      generatedQR={generatedQR}
                      qrData={generatedQR}
                      qrConfig={qrData}
                      onSave={saveQRCode}
                      saveError={saveError}
                      saveSuccess={saveSuccess}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Post-Creation Success Popup */}
      {showSuccessPopup && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Icon name="CheckCircle" size={32} className="text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                QR Code Created Successfully!
              </h3>
              <p className="text-gray-600 mb-6">
                Your QR code "{createdQRCode?.name}" has been saved. What would you like to do next?
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  onClick={handleOpenQRLibrary}
                  className="flex-1 bg-blue-600 text-white hover:bg-blue-700"
                >
                  <Icon name="Eye" size={16} className="mr-2" />
                  View QR Library
                </Button>
                <Button
                  onClick={handleCreateNewQR}
                  variant="outline"
                  className="flex-1"
                >
                  <Icon name="Plus" size={16} className="mr-2" />
                  Create New QR
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QRCodeGenerator;